/**
 * Maps the old flat DETAILS sidebar + top tabs into Overview / Build / Manage.
 *
 * Build stages: setup | sell | enrol | travel
 * layout: 'accordion' = all stage sections on one page as collapsible cards
 *         'workspace' = one item at a time with workspace chrome
 * Manage items: participants | payments | inquiries
 */

export const BUILD_STAGES = [
  {
    key: 'setup',
    label: 'Set up',
    summary: 'Set up',
    layout: 'accordion',
    title: 'Set it up',
    description:
      'Name, programme type, dates and price — the facts the Prosperity page and participant view use. Pills on each field show where that copy appears.',
    groups: [
      {
        key: 'identity',
        kicker: 'Identity',
        description:
          'How the experience is named internally, and how guests see it.',
        itemKeys: ['setup-name'],
      },
      {
        key: 'when-where',
        kicker: 'When & where',
        description:
          'Programme dates, extra dates, duration, group size and location.',
        itemKeys: ['setup-dates', 'setup-details'],
      },
      {
        key: 'investment',
        kicker: 'Investment',
        description: 'Room rates and when payment is collected.',
        itemKeys: ['setup-price'],
      },
      {
        key: 'images',
        kicker: 'Images',
        description:
          'Used in the admin list and at the top of the participant view — not the rotating Prosperity page slider.',
        itemKeys: ['setup-cover'],
      },
    ],
    items: [
      {
        key: 'setup-name',
        label: 'Experience Details',
        type: 'section',
        sectionIndex: 0,
        // Where pills live on individual fields, not the accordion header
        where: [],
      },
      {
        key: 'setup-dates',
        label: 'Programme & dates',
        type: 'section',
        sectionIndex: 3,
        where: ['pub', 'par'],
      },
      {
        key: 'setup-details',
        label: 'Duration, group size, location',
        type: 'section',
        sectionIndex: 7,
        where: ['pub', 'par'],
      },
      {
        key: 'setup-price',
        label: 'Rates & payment',
        type: 'section',
        sectionIndex: 4,
        where: ['pub', 'par'],
      },
      {
        key: 'setup-cover',
        label: 'Header Image',
        type: 'section',
        sectionIndex: 1,
        where: ['par'],
      },
    ],
  },
  {
    key: 'sell',
    label: 'Content',
    summary: 'Content',
    layout: 'accordion',
    title: 'Content',
    description:
      'The story guests see on the Prosperity page before they enroll. Work top to bottom: imagery, what’s included, who teaches, the journey, then FAQs.',
    groups: [
      {
        key: 'prosperity',
        kicker: 'Prosperity page',
        description: 'The rotating hero, inclusions, faculty and journey copy.',
        itemKeys: [
          'content-hero',
          'content-inclusions',
          'content-faculty',
          'content-journey',
          'content-faqs',
        ],
      },
      {
        key: 'interest',
        kicker: 'Interest',
        description:
          'Shown when someone clicks Express Interest on Prosperity. Submissions go to Manage → Inquiries — they are not enrollments.',
        itemKeys: ['content-interest'],
      },
      {
        key: 'extra',
        kicker: 'Extra modules',
        description:
          'Optional Prosperity page sections. Edit only if this experience uses them.',
        itemKeys: [
          'content-rsvp',
          'content-partners',
          'content-video',
          'content-testimonials',
        ],
      },
    ],
    items: [
      {
        key: 'content-hero',
        label: 'Slider images',
        type: 'section',
        sectionIndex: 2,
        where: ['pub'],
      },
      {
        key: 'content-inclusions',
        label: 'Inclusions',
        type: 'section',
        sectionIndex: 9,
        where: ['pub'],
      },
      {
        key: 'content-faculty',
        label: 'Faculty',
        type: 'section',
        sectionIndex: 15,
        where: ['pub', 'par'],
      },
      {
        key: 'content-journey',
        label: 'Your Journey',
        type: 'section',
        sectionIndex: 14,
        where: ['pub'],
      },
      {
        key: 'content-faqs',
        label: 'FAQs',
        type: 'section',
        sectionIndex: 16,
        where: ['pub'],
      },
      {
        key: 'content-interest',
        label: 'Express Interest',
        type: 'section',
        sectionIndex: 5,
        where: ['pub'],
      },
    ],
    optionalLabel: 'Additional details',
    optionalItems: [
      {
        key: 'content-rsvp',
        label: 'RSVP',
        type: 'section',
        sectionIndex: 6,
        optional: true,
        where: ['pub'],
      },
      {
        key: 'content-partners',
        label: 'Partners',
        type: 'section',
        sectionIndex: 8,
        optional: true,
        where: ['pub'],
      },
      {
        key: 'content-video',
        label: 'Main video',
        type: 'section',
        sectionIndex: 12,
        optional: true,
        where: ['pub'],
      },
      {
        key: 'content-testimonials',
        label: 'Testimonials',
        type: 'section',
        sectionIndex: 13,
        optional: true,
        where: ['pub'],
      },
    ],
  },
  {
    key: 'enrol',
    label: 'Enrollment',
    summary: 'Enrollment',
    layout: 'workspace',
    items: [
      {
        key: 'enrol-signup',
        label: 'Signup page',
        type: 'section',
        sectionIndex: 10,
        where: ['par'],
        description:
          'The welcome letter is the first thing an invited guest sees, then the signup page to create their account.',
      },
      {
        key: 'enrol-email',
        label: 'Welcome email',
        type: 'section',
        sectionIndex: 11,
        where: ['par'],
        description:
          'Sent when the Abroad team enrolls someone. The link in the email takes them to signup, and they are enrolled in the Quest automatically.',
      },
      {
        key: 'enrol-form',
        label: 'Enrollment form',
        type: 'page',
        page: 'enrollment-typeform',
        where: ['par'],
        description:
          'Questions participants answer when they enroll. Reorder, add, or remove. Click a question to edit it.',
      },
      {
        key: 'enrol-process',
        label: 'Enrollment process',
        type: 'section',
        sectionIndex: 19,
        where: ['par'],
        description:
          'The steps a participant completes after they enroll. Enable or skip a step, and edit the title and description they see. Click a step to edit it.',
      },
    ],
  },
  {
    key: 'travel',
    label: 'Participant prep',
    summary: 'Participant prep',
    layout: 'workspace',
    items: [
      {
        key: 'prep-learning',
        label: 'Learning Resource',
        type: 'page',
        page: 'learning',
        where: ['par'],
        description:
          'Audio, video and reading attached for participants before they travel. Changes save automatically.',
      },
      {
        key: 'prep-schedule',
        label: 'Itinerary',
        type: 'page',
        page: 'itinerary',
        where: ['par'],
        description:
          'Dated days participants see after they enroll. Open a day, add one image for that day, then timed entries. Copy from another experience to reuse a schedule.',
      },
      {
        key: 'prep-checklist',
        label: 'Travel Checklist',
        type: 'section',
        sectionIndex: 17,
        where: ['par'],
        description:
          'What participants tick off before they travel. Open a section to edit items and notes.',
      },
      {
        key: 'prep-orientation',
        label: 'Orientation Call',
        type: 'section',
        sectionIndex: 18,
        where: ['par'],
        description:
          'The call participants book as part of their enrollment process. Add a link and date when it is ready.',
      },
      {
        key: 'prep-guide-pairing',
        label: 'Wisdom Guide Pairing',
        type: 'page',
        page: 'guide-pairing',
        where: ['admin'],
        description:
          'Pair participants with a wisdom guide and assign one SOW to those pairs. Add an enrollment-form answer as the last column when you need a signal — for example whether they asked for 1:1 coaching.',
      },
    ],
  },
]

export const MANAGE_ITEMS = [
  {
    key: 'manage-participants',
    label: 'Participants',
    page: 'participants',
    pathSuffix: 'participants',
    description:
      'People enrolled on this experience — payments, documents and form responses.',
  },
  {
    key: 'manage-payments',
    label: 'Payments',
    page: 'payment',
    pathSuffix: 'payment',
    description:
      'Transactions recorded on this experience. Filter by outcome, then open a row for the record.',
  },
  {
    key: 'manage-inquiries',
    label: 'Inquiries',
    page: 'inquiries',
    pathSuffix: 'inquiries',
    description:
      'Express Interest submissions. Click a row to read the answers, or enroll someone from here.',
  },
]

export const WHERE_LABELS = {
  admin: 'Admin',
  pub: 'Prosperity',
  par: 'Participant',
}

/** Map where-keys → ExperienceBadge color variant (title stays separate). */
export const WHERE_COLOR_VARIANT = {
  admin: 'taupe',
  pub: 'blue',
  par: 'peach',
}

export const ALL_BUILD_ITEMS = BUILD_STAGES.reduce((acc, stage) => {
  const items = [...stage.items, ...(stage.optionalItems || [])]
  return acc.concat(
    items.map((item) => ({
      ...item,
      stageKey: stage.key,
      stageLabel: stage.label,
      stageSummary: stage.summary,
      stageLayout: stage.layout,
    })),
  )
}, [])

export const getBuildStage = (key) =>
  BUILD_STAGES.find((stage) => stage.key === key) || BUILD_STAGES[0]

export const getBuildItemByKey = (key) =>
  ALL_BUILD_ITEMS.find((item) => item.key === key) || ALL_BUILD_ITEMS[0]

export const getBuildItemBySectionIndex = (index) =>
  ALL_BUILD_ITEMS.find(
    (item) => item.type === 'section' && item.sectionIndex === index,
  )

export const getNextBuildItem = (key) => {
  const idx = ALL_BUILD_ITEMS.findIndex((item) => item.key === key)
  if (idx < 0 || idx >= ALL_BUILD_ITEMS.length - 1) {
    return null
  }
  return ALL_BUILD_ITEMS[idx + 1]
}

/**
 * Resolve current mode + sub-target from pathname (supports legacy URLs).
 */
export const resolveExperienceLocation = (pathname, questId) => {
  const base = questId
    ? `/admin/experiences/${questId}`
    : '/admin/experiences/create-new'

  // Blank create-new defaults to Overview ("Untitled experience"), not Build.
  if (!questId) {
    const buildMatch = pathname.match(/\/create-new\/build(?:\/([^/]+))?$/)
    if (buildMatch) {
      return {
        mode: 'build',
        buildKey: buildMatch[1] || 'setup-name',
        manageKey: null,
        base,
      }
    }
    return {
      mode: 'overview',
      buildKey: null,
      manageKey: null,
      base,
    }
  }

  if (
    pathname === base ||
    pathname === `${base}/` ||
    pathname.endsWith('/overview')
  ) {
    return { mode: 'overview', buildKey: null, manageKey: null, base }
  }

  const buildMatch = pathname.match(/\/build(?:\/([^/]+))?$/)
  if (buildMatch) {
    return {
      mode: 'build',
      buildKey: buildMatch[1] || 'setup-name',
      manageKey: null,
      base,
    }
  }

  const manageMatch = pathname.match(/\/manage(?:\/([^/]+))?$/)
  if (manageMatch) {
    const suffix = manageMatch[1]
    const manageItem =
      MANAGE_ITEMS.find((m) => m.pathSuffix === suffix || m.key === suffix) ||
      MANAGE_ITEMS[0]
    return {
      mode: 'manage',
      buildKey: null,
      manageKey: manageItem.key,
      base,
    }
  }

  // Legacy route aliases
  if (pathname.includes('/details')) {
    return { mode: 'build', buildKey: 'setup-name', manageKey: null, base }
  }
  if (pathname.includes('/enrollment-typeform')) {
    return { mode: 'build', buildKey: 'enrol-form', manageKey: null, base }
  }
  if (pathname.includes('/itinerary')) {
    return { mode: 'build', buildKey: 'prep-schedule', manageKey: null, base }
  }
  if (pathname.includes('/learning')) {
    return { mode: 'build', buildKey: 'prep-learning', manageKey: null, base }
  }
  if (pathname.includes('/participants')) {
    return {
      mode: 'manage',
      buildKey: null,
      manageKey: 'manage-participants',
      base,
    }
  }
  if (pathname.includes('/payment')) {
    return {
      mode: 'manage',
      buildKey: null,
      manageKey: 'manage-payments',
      base,
    }
  }
  if (pathname.includes('/inquiries')) {
    return {
      mode: 'manage',
      buildKey: null,
      manageKey: 'manage-inquiries',
      base,
    }
  }

  return { mode: 'overview', buildKey: null, manageKey: null, base }
}

export const buildExperiencePath = (questId, mode, subKey) => {
  if (!questId) {
    const base = '/admin/experiences/create-new'
    if (mode === 'build') {
      return subKey ? `${base}/build/${subKey}` : `${base}/build`
    }
    return base
  }
  const base = `/admin/experiences/${questId}`
  if (mode === 'overview') {
    return `${base}/overview`
  }
  if (mode === 'build') {
    return subKey ? `${base}/build/${subKey}` : `${base}/build`
  }
  if (mode === 'manage') {
    const item = MANAGE_ITEMS.find((m) => m.key === subKey) || MANAGE_ITEMS[0]
    return `${base}/manage/${item.pathSuffix}`
  }
  return base
}
